import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useGovernanceSettings } from "../../../utils/icGovernance";
import GovernanceLayout from "../Governance/GovernanceLayout";
import SupportTickets from "./SupportTickets";
import { PRIMARY } from "./ticketStatus";
import { SUPPORT_BASE, TICKET_VIEW_URL, isTicketView } from "./supportView";

/**
 * /dashboard/support (and its workbook tabs, nested below it). One page, two
 * faces, decided only by the server's `/ic-ticket/governance/me` answer:
 * governance on (admins) → the project workbook (`GovernanceLayout`, index →
 * tracker); otherwise → the Support ticket list, unchanged. `?view=tickets`
 * forces the ticket list for anyone. One spinner while the settings load, so
 * an admin never sees the ticket list flash before the workbook.
 */

export const SupportLoading = () => (
  <Box sx={{ padding: "20px", textAlign: "center" }}>
    <CircularProgress sx={{ color: PRIMARY, mt: 6 }} />
  </Box>
);

const ProjectViewLink = () => (
  <Button
    component={Link}
    to={SUPPORT_BASE}
    size="small"
    sx={{ textTransform: "none", color: "#8a8a93", whiteSpace: "nowrap" }}
  >
    Project view
  </Button>
);

const SupportHub = () => {
  const { settings, loading, error } = useGovernanceSettings();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const atRoot = pathname.replace(/\/+$/, "") === SUPPORT_BASE;

  // Escape hatch: the plain ticket list, no waiting on the settings (the
  // "Project view" link appears once they say governance is on).
  if (isTicketView(searchParams)) {
    if (!atRoot) return <Navigate to={TICKET_VIEW_URL} replace />;
    return <SupportTickets headerLink={settings.enabled ? <ProjectViewLink /> : undefined} />;
  }

  if (loading) return <SupportLoading />;

  if (error) {
    return (
      <Box sx={{ padding: { xs: "16px", sm: "20px" }, textAlign: "left" }}>
        <Alert severity="error">
          {error}
          <Box sx={{ mt: 1 }}>
            <Button component={Link} to={TICKET_VIEW_URL} size="small" variant="outlined" color="inherit">
              Open support tickets instead
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (settings.enabled) return <GovernanceLayout settings={settings} />;

  // No workbook: the tab URLs have nothing to show.
  if (!atRoot) return <Navigate to={SUPPORT_BASE} replace />;
  return (
    <SupportTickets
      banner={
        settings.reason === "not_configured" ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            The project workbook is off because IC_TICKET_API_KEY is not set on the server.
          </Alert>
        ) : undefined
      }
    />
  );
};

export default SupportHub;
